// Una casilla de una COLUMNA PROPIA, en cualquiera de las dos tablas que las
// tienen: Renovaciones y Prospecto y Referido (Lissette 2026-09-09).
//
// Existe para que las dos guarden IGUAL. Sin esto, cada tabla llevaría su copia
// del guardar-al-salir, y el día que una gane un arreglo la otra se queda atrás
// sin que nada avise — que es lo que ya pasó con el ternario de la constancia.
//
// CÓMO GUARDA. Al salir de la casilla (blur), y solo si el texto CAMBIÓ. Un
// guardado por tecleo llenaría la red de llamadas y machacaría lo que otra
// persona esté escribiendo en la misma fila.
//
// SI FALLA, SE DICE Y SE DEVUELVE EL TEXTO ANTERIOR. Dejar en pantalla algo que
// no se guardó es peor que perderlo: el equipo lo da por hecho y nadie vuelve.
//
// LOS TRES TIPOS (Lissette 2026-09-10). La casilla se pinta según el tipo de la
// columna — texto, desplegable o marcar —, pero las tres guardan por el MISMO
// camino (`guardar`), así que el aviso de fallo y la vuelta atrás son idénticos:
//
//   text  — lo de siempre.
//   list  — el `Combobox` compartido, el mismo de Aseguradora y Tipo de
//           suplemento: sugiere, pero acepta cualquier texto. Las opciones se las
//           pasa la pantalla con `opcionesDeColumna` (lo que la agencia ya
//           escribió), así que la lista se va creando sola.
//   check — se guarda al instante, no al salir: una casilla que se marca y no se
//           mueve de sitio no tiene un "salir" en el que guardar.

import { useEffect, useRef, useState } from 'react';
import { toast } from './Toast.jsx';
import { Combobox } from './ui.jsx';
import { MARCA_SI, estaMarcada, tipoDeColumna } from '../customColumns.js';

/**
 * La misma columna propia, pero DENTRO DE UN FORMULARIO (Seguimientos, Baúl e
 * Info de Empresa guardan la fila entera al dar a Guardar, no casilla a casilla).
 *
 * Es controlado y no guarda nada: eso lo hace el formulario. Existe para que los
 * tres modales pinten los tres tipos IGUAL — sin él, cada uno tendría su propia
 * idea de qué es un "check", y el día que uno gane un arreglo los otros dos se
 * quedan atrás sin que nada avise.
 */
export function CampoDeColumnaPropia({ columna, valor = '', onChange, opciones = [], lang = 'es', inputStyle, className = 'dkt-inp' }) {
  const es = lang === 'es';
  const tipo = tipoDeColumna(columna);

  if (tipo === 'check') {
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)' }}>
        <input type="checkbox" checked={estaMarcada(valor)}
          onChange={(e) => onChange && onChange(e.target.checked ? MARCA_SI : '')}
          style={{ margin: 0, width: 15, height: 15, accentColor: 'var(--c-cyan,#00d4ff)' }} />
        {estaMarcada(valor) ? (es ? 'Sí' : 'Yes') : (es ? 'No' : 'No')}
      </label>
    );
  }

  if (tipo === 'list') {
    return (
      <Combobox value={String(valor || '')} options={opciones} maxLength={500}
        onChange={(v) => onChange && onChange(v)}
        placeholder={es ? 'Escribe o elige…' : 'Type or pick…'}
        className={className} inputStyle={inputStyle} />
    );
  }

  return (
    <input className={className} value={String(valor || '')} maxLength={500} autoComplete="off"
      onChange={(e) => onChange && onChange(e.target.value)}
      style={inputStyle} />
  );
}

export default function CeldaPropia({
  valor = '',
  onGuardar,          // async (texto) => void
  lang = 'es',
  disabled = false,
  estilo = {},
  placeholder = '',
  columna = null,     // { id, name, type, options } — sin ella, texto libre
  opciones = [],      // lo que ofrece el desplegable de una columna `list`
}) {
  const es = lang === 'es';
  const tipo = tipoDeColumna(columna);
  const [texto, setTexto] = useState(valor || '');
  const [guardando, setGuardando] = useState(false);
  // Lo último confirmado. Es contra esto que se compara al salir, y a esto que
  // se vuelve si el guardado falla.
  const confirmado = useRef(valor || '');
  // Si ESTA casilla tiene el foco. Se mira antes de aceptar un valor nuevo del
  // servidor: ponerla al día mientras alguien escribe le borraría lo tecleado.
  const enfocada = useRef(false);

  // Si la fila se recarga desde el servidor con otro valor (otra persona lo
  // cambió), la casilla se pone al día — salvo mientras se está escribiendo.
  useEffect(() => {
    if (enfocada.current) return;
    setTexto(valor || '');
    confirmado.current = valor || '';
  }, [valor]);

  // El único camino de guardado de los tres tipos. Devuelve si se guardó, para
  // que quien llame sepa si tiene que deshacer lo que pintó.
  const guardar = async (nuevo) => {
    const limpio = String(nuevo || '').trim();
    if (limpio === confirmado.current) return true;
    setGuardando(true);
    try {
      await onGuardar(limpio);
      confirmado.current = limpio;
      setTexto(limpio);
      return true;
    } catch (fallo) {
      // El nombre NO puede ser `e`: en `salir` `e` es el evento del blur, y
      // pisarlo allí dejaría el marco de la casilla puesto para siempre.
      setTexto(confirmado.current);
      toast.error(
        es ? 'No se pudo guardar la casilla. Se dejó como estaba.'
           : "Couldn't save the cell. It was left as it was.",
      );
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const salir = async (e) => {
    enfocada.current = false;
    // Al salir se quita el marco: si no, la casilla se queda pintada como si
    // siguiera en edicion y no se sabe donde esta el cursor.
    //
    // SOLO en la casilla de texto, que es la que lo pinta al entrar. Hacerlo
    // tambien en el desplegable le borraria el fondo que le pone la pantalla que
    // lo usa (Pagos del Mes le pinta una caja): son estilos escritos a mano
    // encima del elemento, y React no los vuelve a poner porque para el no
    // cambio nada.
    if (tipo === 'text' && e?.currentTarget) { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.background = 'transparent'; }
    await guardar(texto);
  };

  // ── Marcar / desmarcar ────────────────────────────────────────────────────
  // Se guarda en el acto y se pinta lo que se acaba de tocar. Si el servidor lo
  // rechaza, `guardar` devuelve el valor anterior y avisa.
  if (tipo === 'check') {
    const marcada = estaMarcada(texto);
    return (
      <label
        title={columna?.name || ''}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', cursor: disabled || guardando ? 'default' : 'pointer', opacity: guardando ? 0.6 : 1, ...estilo }}
      >
        <input
          type="checkbox"
          checked={marcada}
          disabled={disabled || guardando}
          onChange={async (e) => {
            const siguiente = e.target.checked ? MARCA_SI : '';
            setTexto(siguiente);
            const ok = await guardar(siguiente);
            if (!ok) setTexto(confirmado.current);
          }}
          style={{ margin: 0, width: 15, height: 15, accentColor: 'var(--c-cyan,#00d4ff)', cursor: 'inherit' }}
        />
      </label>
    );
  }

  const estiloDeCaja = {
    width: '100%', background: 'transparent', border: '1px solid transparent',
    borderRadius: 6, padding: '3px 6px', fontSize: '.8rem',
    color: 'var(--c-text,#c8dff0)', outline: 'none', boxSizing: 'border-box',
    opacity: guardando ? 0.6 : 1,
    ...estilo,
  };

  // ── Desplegable que se llena solo ─────────────────────────────────────────
  // `onPick` guarda al elegir con el ratón: elegir una opción NO dispara blur
  // (el menú se queda con el foco a propósito), así que sin esto la elección se
  // quedaría sin guardar hasta que la persona picara en otro sitio.
  if (tipo === 'list') {
    return (
      <div style={{ width: '100%' }} onFocusCapture={() => { enfocada.current = true; }}>
        <Combobox
          value={texto}
          options={opciones}
          className=""
          disabled={disabled || guardando}
          maxLength={500}
          title={columna?.name || ''}
          placeholder={placeholder || '—'}
          onChange={(v) => setTexto(v)}
          onPick={(v) => { enfocada.current = false; guardar(v); }}
          onBlur={salir}
          inputStyle={estiloDeCaja}
        />
      </div>
    );
  }

  return (
    <input
      data-celda-propia="si"
      value={texto}
      disabled={disabled || guardando}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={salir}
      // Enter guarda sin tener que ir a picar fuera; Escape deshace.
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.currentTarget.blur(); }
        else if (e.key === 'Escape') { setTexto(confirmado.current); e.currentTarget.blur(); }
      }}
      placeholder={placeholder || (es ? '—' : '—')}
      maxLength={500}
      style={estiloDeCaja}
      onFocus={(e) => { enfocada.current = true; e.currentTarget.style.borderColor = 'var(--c-cyan,#00d4ff)'; e.currentTarget.style.background = 'var(--c-surface2,#112538)'; }}
      onMouseEnter={(e) => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = 'var(--c-border,#1a3550)'; }}
      onMouseLeave={(e) => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = 'transparent'; }}
    />
  );
}
